import React from 'react';
import Sparkline from './Sparkline';
import AutoFitText from './AutoFitText';
import { KpiCard } from '../design-system';

const VALUE_MAX_FONT_PX = { xl: 44, lg: 34, md: 24 };
const VALUE_MIN_FONT_PX = 12;

/**
 * Premium KPI card — wraps design-system KpiCard with sparkline/delta support.
 */
export default function Metric({
  label,
  value,
  unit,
  delta,
  context,
  trend,
  trendColor,
  size = 'lg',
  align = 'left',
  valueAutoFit = false,
  className = '',
}) {
  const badge = delta ? <DeltaChip {...delta} /> : null;

  const footer = (context || (trend && trend.length > 1)) ? (
    <div className="flex items-end justify-between gap-3 w-full mt-1">
      {context && <span>{context}</span>}
      {trend && trend.length > 1 && (
        <div style={{ color: trendColor || 'var(--text-muted)' }}>
          <Sparkline points={trend} width={88} height={22} area />
        </div>
      )}
    </div>
  ) : null;

  if (valueAutoFit) {
    return (
      <div className={`app-kpi-card ${className}`.trim()} style={{ textAlign: align === 'center' ? 'center' : 'left' }}>
        <div className="app-kpi-card__top">
          <span className="app-kpi-card__eyebrow app-readable">{label}</span>
          {badge && <div className="app-kpi-card__top-right">{badge}</div>}
        </div>
        <AutoFitText
          preferWrap={false}
          className="app-kpi-card__number font-semibold tracking-tight app-numeric"
          style={{ color: 'var(--text-primary)', maxWidth: '100%' }}
          minFontSize={VALUE_MIN_FONT_PX}
          maxFontSize={VALUE_MAX_FONT_PX[size] ?? VALUE_MAX_FONT_PX.lg}
        >
          {value}{unit ? ` ${unit}` : ''}
        </AutoFitText>
        {footer}
      </div>
    );
  }

  return (
    <KpiCard
      eyebrow={label}
      value={value}
      unit={unit}
      context={typeof footer === 'object' ? undefined : context}
      badge={badge}
      className={className}
    >
      {footer}
    </KpiCard>
  );
}

function DeltaChip({ value, direction, suffix = '' }) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  const dir = direction || (v > 0 ? 'up' : v < 0 ? 'down' : 'flat');
  const toneClass = 'app-kpi-badge--orange';

  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '•';
  const abs = Math.abs(v);
  return (
    <span className={`app-kpi-badge ${toneClass} app-numeric`}>
      {arrow} {abs}{suffix}
    </span>
  );
}
